import { get } from '../util/secure_fetch.js';
import { registerEventHandlers as registerShareableLibraryHandlers } from './mixins/ShareableLibrary.js';

class LibrariesPageLoader extends HTMLElement {
  connectedCallback() {
    // Create a loader element to trigger the next page of libraries to load
    const loader = document.createElement('div');
    const progressCircle = document.createElement('sp-progress-circle');
    progressCircle.setAttribute('indeterminate', '');
    // Give the loader a height so it can be observed
    loader.style.background = 'var(--spectrum-gray-100) ';
    loader.style.padding = '10px';
    loader.style.textAlign = 'center';
    progressCircle.style.visibility = 'hidden';
    loader.appendChild(progressCircle);
    const shadowRoot = this.attachShadow({mode: 'open'});
    shadowRoot.appendChild(loader);

    const cardList = document.querySelector('af-library-card-list');
    const totalLibraryCount = this.getAttribute('totalLibraryCount') || 0;
    let currentPage = 1;

    this.observer = new IntersectionObserver(async (entries) => {
      // If we have loaded all the libraries, stop observing
      if (cardList.libraries?.length >= totalLibraryCount) {
        this.observer.unobserve(loader);
        return;
      }

      // If the loader is intersecting, fetch the next page of libraries
      if (entries[0].intersectionRatio > 0 && entries[0].intersectionRatio <= 1) {
        try {
          progressCircle.style.visibility = 'visible';
          const response = await get(`/libraries.json?page=${++currentPage}`);
          const data = await response.json();

          cardList.libraries = this.sortLibraries(cardList.libraries, data.libraries);

          // FONTS-11379: REMOVE `onlyPublicLibraries` WHEN PUBLIC LIBRARY PAGING IS FIXED
          // If there are no libraries, stop observing
          const onlyPublicLibraries = data.libraries?.every(library => library.ownership === 'public');
          if (data.libraries?.length === 0 || onlyPublicLibraries) {
            this.observer.unobserve(loader);
          }
        } catch (error) {
          console.error('Failed to fetch libraries', error);
        } finally {
          progressCircle.style.visibility = 'hidden';
        }
      }
    });
    this.observer.observe(loader);

    registerShareableLibraryHandlers();
  }

  // Sort the libraries by public and private, and de-duplicate the list
  sortLibraries(cardLibraries, newLibraries) {
    if (!newLibraries) {
      return cardLibraries;
    }

    const privateLibraries = new Map();
    const publicLibraries = new Map();
    const libraries = [...cardLibraries, ...newLibraries];

    libraries.forEach((library) => {
      if (library.ownership === 'public') {
        publicLibraries.set(library.library_urn, library);
      } else {
        privateLibraries.set(library.library_urn, library);
      }
    });
    return [...privateLibraries.values()].concat([...publicLibraries.values()]);
  }
}

customElements.define('af-libraries-page-loader', LibrariesPageLoader);
